/*
 * Put the pack upgrades inside the cart drawer the upsell app draws.
 *
 * The app replaces the theme's drawer with its own, and nothing the theme renders can
 * reach inside it. This fetches the offers from the theme, server rendered for the
 * current cart, and places each one directly under the line it belongs to.
 *
 * Under its own line, not collected at the bottom. A cart with three products and a
 * list of three offers underneath gives the customer no way to tell which offer belongs
 * to which product, and the offers are about swapping a specific line.
 *
 * Lines are matched by variant id, read from the product link the app puts in each row,
 * and by position when a cart holds the same variant on more than one line.
 *
 * Rules this file holds itself to, because it is writing into markup owned by someone
 * else's app:
 *
 *   - Never throw into their code. Everything is wrapped, and a failure leaves their
 *     drawer exactly as it was.
 *   - Never move, wrap or restyle anything of theirs. It appends elements and removes
 *     only the elements it appended.
 *   - Fail silent. If the anchors are gone after one of their releases, the offers
 *     simply do not appear. The cart still works and nothing looks broken.
 *
 * The click handler is the shared one in cart-upgrades.js, which listens on `document`,
 * so the injected markup needs no wiring of its own.
 *
 * If this turns out to need constant repair, that is the signal to stop and use an app
 * built for the job rather than to add more selectors here.
 */
(function () {
  var DRAWER = ".ocu-cart-drawer";
  var ROW = ".ocu-cart-line-item-wrapper";
  var MARK = "data-wn-upgrades";

  var pending = false;
  var lastToken = null;
  var lastPlaced = 0;

  /*
   * Two ways a refresh used to get lost, both ending in a stale drawer until the
   * customer reloaded the page.
   *
   * `dirty`: the rows changed while a fetch was already in flight. The old code
   * dropped that second request on the floor, and because `lastToken` had
   * already been advanced, nothing asked again. Now the in-flight fetch notes
   * that it is out of date and runs once more when it lands.
   *
   * `retries`: the app redraws its drawer the moment a line is removed, before
   * the cart has finished changing on the server. A fragment fetched in that
   * gap is rendered for the cart as it was, so an offer that should have come
   * back does not. The fragment reports how many lines it was rendered for; if
   * that disagrees with the rows on screen, fetch again after a short pause,
   * at most twice.
   */
  var dirty = false;
  var retries = 0;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function rows() {
    return Array.prototype.slice.call(document.querySelectorAll(DRAWER + " " + ROW));
  }

  function drawerIsOpen() {
    var drawer = document.querySelector(DRAWER);
    if (!drawer) return false;
    if (drawer.classList.contains("ocu-visible")) return true;
    var box = drawer.getBoundingClientRect();
    return box.width > 0 && box.right > 0 && box.left < window.innerWidth;
  }

  function clear() {
    var mine = document.querySelectorAll("[" + MARK + "]");
    for (var i = 0; i < mine.length; i++) {
      if (mine[i].parentNode) mine[i].parentNode.removeChild(mine[i]);
    }
  }

  // The variant a row is showing, taken from the product link the app renders in it.
  function rowVariant(row) {
    var link = row.querySelector('a[href*="variant="]');
    if (!link) return null;
    var match = /[?&]variant=(\d+)/.exec(link.getAttribute("href") || "");
    return match ? match[1] : null;
  }

  /*
   * The schedule a row is on, read off the app's own control.
   *
   * The variant alone is not enough to identify a line. The same product sitting in the
   * cart twice, once one-time and once on a subscription, is an ordinary cart here
   * because the product page defaults to Subscribe and Save. Matching on the variant
   * alone put each line's offer under the other one, so a one-time line quoted
   * subscription prices and the subscriber was quoted the one-off ones.
   *
   * The app shows a select of schedules on a subscription line and its own upgrade
   * button on a one-time line, which is exactly the distinction needed.
   */
  function rowPlan(row) {
    var select = row.querySelector(".ocu-cart-line-subscription-dropdown__select");
    if (select) {
      var option = select.options[select.selectedIndex];
      return option ? option.text.replace(/\s+/g, " ").trim() : "";
    }
    return "";
  }

  function normalise(value) {
    return (value || "").replace(/\s+/g, " ").trim().toLowerCase();
  }

  function place(groups) {
    var open = rows();
    if (!open.length) return;

    var used = {};
    var placed = 0;

    for (var i = 0; i < groups.length; i++) {
      var group = groups[i];
      var variant = group.getAttribute("data-line-variant");
      var plan = normalise(group.getAttribute("data-line-plan"));
      var index = parseInt(group.getAttribute("data-line-index"), 10);
      var target = null;

      // Variant and schedule together, which is what actually identifies a cart line.
      for (var r = 0; r < open.length; r++) {
        if (used[r]) continue;
        if (variant && rowVariant(open[r]) === variant && normalise(rowPlan(open[r])) === plan) {
          target = r;
          break;
        }
      }

      // Variant alone, for a cart that holds it only once.
      if (target === null) {
        for (var v = 0; v < open.length; v++) {
          if (used[v]) continue;
          if (variant && rowVariant(open[v]) === variant) {
            target = v;
            break;
          }
        }
      }

      // Position as written by the same Liquid loop that renders their rows.
      if (target === null && !isNaN(index) && index < open.length && !used[index]) {
        target = index;
      }

      if (target === null) continue;

      used[target] = true;
      placed++;

      // The spare purchase option control is only wanted where the app failed to draw
      // its own, which is any pack it had not rendered when the page loaded.
      var spare = group.querySelector(".wn-plan--fallback");
      if (spare && open[target].querySelector(".ocu-cart-line-subscription-dropdown__wrapper")) {
        spare.parentNode.removeChild(spare);
      }

      group.setAttribute(MARK, "");
      open[target].parentNode.insertBefore(group, open[target].nextSibling);
    }

    return placed;
  }

  /*
   * The companion product goes after the last line, below any pack offer that
   * was just placed under it. It belongs to the cart rather than to a line, so
   * it is not matched to a row the way the pack groups are. Marked with the same
   * attribute so `clear()` removes it with the rest on every refresh.
   */
  function placeCross(cross) {
    if (!cross) return 0;
    var open = rows();
    if (!open.length) return 0;

    var anchor = open[open.length - 1];
    while (anchor.nextElementSibling && anchor.nextElementSibling.hasAttribute(MARK)) {
      anchor = anchor.nextElementSibling;
    }

    cross.setAttribute(MARK, "");
    anchor.parentNode.insertBefore(cross, anchor.nextSibling);
    return 1;
  }

  function refresh() {
    if (pending) {
      dirty = true;
      return;
    }
    if (!rows().length) return;

    pending = true;

    fetch(root() + "?section_id=cart-upgrades-fragment")
      .then(function (response) {
        if (!response.ok) throw new Error("fragment failed");
        return response.text();
      })
      .then(function (html) {
        pending = false;

        var parsed = new DOMParser().parseFromString(html, "text/html");

        // Rendered for a different cart than the one on screen: the fetch beat the
        // change. Ask again rather than placing offers for a cart that no longer
        // exists.
        var wrapper = parsed.querySelector(".wn-cart-fragment");
        var expected = wrapper ? parseInt(wrapper.getAttribute("data-lines"), 10) : NaN;
        if (!isNaN(expected) && expected !== rows().length && retries < 2) {
          retries++;
          window.setTimeout(refresh, 500);
          return;
        }
        retries = 0;

        var groups = Array.prototype.slice.call(parsed.querySelectorAll(".wn-upsell__group"));
        var cross = parsed.querySelector(".wn-crosssell");

        clear();
        var placed = groups.length ? place(groups) : 0;
        placed += placeCross(cross);
        lastPlaced = placed;

        // The rows changed, so the cart did. The header's count is the theme's
        // and the app does not update it; the helper in cart-upgrades.js does.
        if (typeof window.wnRefreshCartBubble === "function") window.wnRefreshCartBubble();

        if (dirty) {
          dirty = false;
          refresh();
        }
      })
      .catch(function () {
        pending = false;
        // Silence is the correct outcome. Their drawer is not ours to put errors in.
        if (dirty) {
          dirty = false;
          refresh();
        }
      });
  }

  // Their drawer redraws for reasons that do not change the cart, such as opening and
  // closing. Keying on the rows keeps this from refetching on every one of those.
  function token() {
    var open = rows();
    if (!open.length) return null;

    var parts = [];
    for (var i = 0; i < open.length; i++) {
      var clone = open[i].cloneNode(true);
      var mine = clone.querySelectorAll("[" + MARK + "]");
      for (var m = 0; m < mine.length; m++) mine[m].remove();
      parts.push(clone.textContent.replace(/\s+/g, ""));
    }
    return parts.join("|");
  }

  /*
   * Drop the spare purchase option from any row where the app has since drawn its own.
   *
   * Placement and the app's own render are not in step: the offer is injected a moment
   * after the drawer's rows appear, and the app fills in its schedule control after
   * that, so deciding once at injection time left two controls stacked on rows that
   * only needed one. Cheap enough to redo on every pass, and it touches nothing but the
   * element this file added.
   */
  function prune() {
    var open = rows();
    for (var i = 0; i < open.length; i++) {
      if (!open[i].querySelector(".ocu-cart-line-subscription-dropdown__wrapper")) continue;

      var next = open[i].nextElementSibling;
      if (!next || !next.hasAttribute || !next.hasAttribute(MARK)) continue;

      var spare = next.querySelector(".wn-plan--fallback");
      if (spare) spare.parentNode.removeChild(spare);
    }
  }

  function check() {
    try {
      prune();

      if (!drawerIsOpen()) {
        lastToken = null;
        return;
      }

      var now = token();
      if (now === null) return;

      /*
       * An unchanged cart is not on its own a reason to sit still.
       *
       * The app rebuilds its drawer for its own reasons, and a rebuild throws away the
       * injected offers with everything else. The token deliberately ignores those
       * offers, so it reads as unchanged, and keying only on it meant the offers
       * vanished on the first redraw and never came back.
       *
       * So also act when the offers are gone from a drawer that had them. `lastPlaced`
       * of zero means the last fetch legitimately had nothing to show, such as a cart
       * holding only the largest pack, and that must not turn into a fetch on every
       * mutation the page makes.
       */
      var mineMissing = lastPlaced > 0 && !document.querySelector("[" + MARK + "]");
      if (now === lastToken && !mineMissing) return;

      lastToken = now;
      refresh();
    } catch (error) {
      // Never let this file break a page it does not own.
    }
  }

  var scheduled = null;
  function schedule() {
    if (scheduled) return;
    scheduled = window.setTimeout(function () {
      scheduled = null;
      check();
    }, 120);
  }

  function start() {
    try {
      new MutationObserver(schedule).observe(document.body, { childList: true, subtree: true });
      schedule();
    } catch (error) {
      /* nothing to do */
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start);
  } else {
    start();
  }
})();
